import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { getSettingThrottledQuickAdds } from "@faire/retailer-visitor-shared/settings/getSettingEnableThrottledQuickAdds";
import { useBrandCartQueryParams } from "@faire/retailer-visitor-shared/views/brand/BrandCart/hooks/useBrandCart";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { getQueryKey as getCartQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get-hooks";
import { IBrandCartV2 } from "@faire/web-api--source/faire/carts/IBrandCartV2";
import { ICartItemV2 } from "@faire/web-api--source/faire/carts/ICartItemV2";
import { ICartV2 } from "@faire/web-api--source/faire/carts/ICartV2";
import { IShipmentGroup } from "@faire/web-api--source/faire/carts/IShipmentGroup";
import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useCallback } from "react";

export const useOptimisticBrandCartUpdate = (brandToken: string) => {
  const queryClient = useQueryClient();
  const buildQueryParams = useBrandCartQueryParams();

  const addBrandCartItems = useCallback(
    async (items: ICartItemV2[]) => {
      const queryKey = getCartQueryKey(
        getCartToken(),
        buildQueryParams({ brandToken })
      );

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<ICartV2 | undefined>(queryKey, (data) => {
        if (!data) {
          return undefined;
        }
        const brandCart = data.brand_carts.find(
          (cart) => cart.brand_token === brandToken
        );
        if (!brandCart) {
          return undefined;
        }
        return updateItemsInBrandCart(data, brandCart, items);
      });
    },
    [queryClient, buildQueryParams, brandToken]
  );

  return {
    addBrandCartItems,
  };
};

const updateItemsInBrandCart = (
  cart: ICartV2,
  brandCart: IBrandCartV2,
  items: ICartItemV2[]
) => {
  return produce(cart, (draft) => {
    let updatedCartItems: Record<string, ICartItemV2> = Object.values(
      brandCart.cart_items
    )
      .filter(isNotUndefined)
      .reduce(
        (acc, item) => ({
          ...acc,
          [item.product_token + "_" + item.product_option_token ?? ""]: item,
        }),
        {}
      );

    items.forEach((item) => {
      const token = item.product_token + "_" + item.product_option_token ?? "";
      if (item.quantity === 0) {
        delete updatedCartItems[token];
      } else {
        if (getSettingThrottledQuickAdds() && !(token in updatedCartItems)) {
          // Insert new item at the top of the list
          updatedCartItems = {
            [token]: ICartItemV2.build({
              ...item,
              token,
            }),
            ...updatedCartItems,
          };
        } else {
          // Insert new item at the bottom of the list
          updatedCartItems[token] = ICartItemV2.build({
            ...item,
            token,
          });
        }
      }
    });

    const tokensToRemove = new Set(
      items
        .filter((item) => item.quantity === 0)
        .map(
          (item) => item.product_token + "_" + item.product_option_token ?? ""
        )
    );

    const newBrandCart = IBrandCartV2.build({
      ...brandCart,
      cart_items: updatedCartItems,
      last_updated_by_retailer_at: Date.now(),
      shipment_groups: [
        IShipmentGroup.build({
          ...brandCart.shipment_groups[0],
          cart_item_tokens: [
            ...(brandCart.shipment_groups[0]?.cart_item_tokens ?? []).filter(
              (token) => !tokensToRemove.has(token)
            ),
            ...Object.keys(updatedCartItems).filter(
              (token) => !brandCart.cart_items[token]
            ),
          ],
        }),
        ...brandCart.shipment_groups.slice(1),
      ],
    });
    draft.brand_carts = [newBrandCart, ...draft.brand_carts];
  });
};
